/**
 * What the content chat tells the user when the server said why a send
 * failed: which file it refused for good and why, or what to wait for
 * before the same send can go through.
 *
 * The codes are read when a send fails, not at module load: every surface
 * that loads the notes folder loads this module, send or no send.
 */
import {
  FILE_TOO_LARGE_CODE,
  FILE_TYPE_NOT_ALLOWED_CODE,
  FRAME_TOO_LARGE_CODE,
  IMAGE_DIMENSIONS_NOT_ALLOWED_CODE,
  RATE_LIMITED_CODE,
  UPLOAD_CONCURRENCY_EXCEEDED_CODE,
  UPLOAD_QUOTA_EXCEEDED_CODE,
  type MessageSendError,
} from '@discovr/features';

import { IMAGE_MAX_PIXELS, IMAGE_MAX_SIDE_PX, IMAGE_TOO_LARGE_CODE } from '../../../../../lib/media/compressImage';

const MEGAPIXEL = 1_000_000;

const FRAME_REFUSED_TOAST = 'The captured frame is too large to send. Remove it and capture the frame again.';
/**
 * Unsent uploads count against the quota until a message sends them or they
 * expire, which can take a day — waiting a few minutes clears nothing.
 */
const UPLOAD_QUOTA_TOAST =
  "You have too many uploaded files that haven't been sent yet. Send this message with fewer files, or try again later.";
const UPLOAD_CONCURRENCY_TOAST = 'Too many uploads are running at once. Wait for them to finish, then try again.';
const RATE_LIMITED_TOAST = "You're sending too quickly. Wait a moment, then try again.";

/**
 * The server's own limit may have moved since the policy was read, so this
 * never names a size.
 */
function fileTooLargeRefusal(name: string): string {
  return `"${name}" is too large to attach.`;
}

/** The picker already let the extension through, so what's wrong is what's inside. */
function fileContentsRefusal(name: string): string {
  return `"${name}" can't be attached: its contents don't match its file type.`;
}

function imageTooLargeRefusal(name: string): string {
  return `"${name}" is too large to attach: images can be up to ${IMAGE_MAX_SIDE_PX} px on a side and ${IMAGE_MAX_PIXELS / MEGAPIXEL} megapixels.`;
}

function imageDimensionsRefusal(name: string): string {
  return `"${name}" can't be attached: its image dimensions aren't supported.`;
}

/** Copy for a file the server refused for good, by error code; each names the file. */
function refusedFileCopy(code: string): ((name: string) => string) | undefined {
  if (code === FILE_TOO_LARGE_CODE) return fileTooLargeRefusal;
  if (code === FILE_TYPE_NOT_ALLOWED_CODE) return fileContentsRefusal;
  if (code === IMAGE_DIMENSIONS_NOT_ALLOWED_CODE) return imageDimensionsRefusal;
  if (code === IMAGE_TOO_LARGE_CODE) return imageTooLargeRefusal;
  return undefined;
}

/** Copy for a failure no one file explains: the message's frame, or the server holding sends back. */
function unnamedFailureCopy(code: string): string | undefined {
  if (code === FRAME_TOO_LARGE_CODE) return FRAME_REFUSED_TOAST;
  if (code === UPLOAD_QUOTA_EXCEEDED_CODE) return UPLOAD_QUOTA_TOAST;
  if (code === UPLOAD_CONCURRENCY_EXCEEDED_CODE) return UPLOAD_CONCURRENCY_TOAST;
  if (code === RATE_LIMITED_CODE) return RATE_LIMITED_TOAST;
  return undefined;
}

/** A refusal that must name its file says nothing when the error doesn't name one. */
function refusedFileMessage(code: string, fileName: string | undefined): string | undefined {
  if (!fileName) return undefined;
  return refusedFileCopy(code)?.(fileName);
}

/** A gateway's 413 or 429 carries no code of ours; the classification, read from its status, still says what happened. */
function statusFailureCopy({ kind, fileName }: MessageSendError): string | undefined {
  if (kind === 'file_rejected' && fileName) return fileTooLargeRefusal(fileName);
  if (kind === 'throttled') return RATE_LIMITED_TOAST;
  return undefined;
}

/**
 * What to tell the user about a failed send, when the server said why — by its
 * code, or by the status a gateway answered with. Undefined for anything else
 * — the chat's own failure banner covers it.
 */
export function sendFailureMessage(err: MessageSendError): string | undefined {
  const { code, fileName } = err;
  const byCode = code ? (refusedFileMessage(code, fileName) ?? unnamedFailureCopy(code)) : undefined;
  return byCode ?? statusFailureCopy(err);
}
